export type ShowcaseMediaType = 'image' | 'gallery' | 'video' | 'longform' | 'link';

export interface RelatedCaseStudy {
  slug: string;
  hookCopy: string;
}

interface BaseShowcasePiece {
  id: string;
  title: string;
  context: string;
  thumbnail: string;
  related?: RelatedCaseStudy;
}

export interface ImagePiece extends BaseShowcasePiece {
  mediaType: 'image';
  fullImage: string;
  caption?: string;
}

/**
 * Gallery items are discriminated by `type` so a single gallery can mix
 * imagery and video (YouTube or Vimeo). Useful for campaign tiles like
 * We See You where the anthem film, portrait films, experiential, and
 * OOH all belong under one curated sequence.
 *
 * For video items: provide either youtubeId or vimeoId. `youtubeStart`
 * is an optional seconds-offset that only applies to YouTube.
 */
export type GalleryItem =
  | { type: 'image'; src: string; caption?: string }
  | {
      type: 'video';
      youtubeId?: string;
      vimeoId?: string;
      youtubeStart?: number;
      caption?: string;
    };

export interface GalleryPiece extends BaseShowcasePiece {
  mediaType: 'gallery';
  items: GalleryItem[];
}

export interface VideoPiece extends BaseShowcasePiece {
  mediaType: 'video';
  // Provide one of youtubeId, vimeoId, or videoSrc. youtubeStart is an
  // optional seconds-offset for YouTube only.
  youtubeId?: string;
  vimeoId?: string;
  youtubeStart?: number;
  videoSrc?: string;
  poster?: string;
  caption?: string;
}

export interface LongformPiece extends BaseShowcasePiece {
  mediaType: 'longform';
  pages: Array<{ src: string; caption?: string }>;
  intro?: string;
}

/**
 * A piece that lives somewhere else. The tile renders as an anchor and
 * opens the live thing in a new tab rather than a modal — there's no
 * in-page media to show, and a screenshot would be a worse version of
 * the product itself.
 */
export interface LinkPiece extends BaseShowcasePiece {
  mediaType: 'link';
  url: string;
}

export type ShowcasePiece = ImagePiece | GalleryPiece | VideoPiece | LongformPiece | LinkPiece;

export const showcasePieces: ShowcasePiece[] = [
  {
    id: 'avenue-m-platform',
    title: 'Valuation Platform',
    context: 'Avenue M',
    thumbnail: '/assets/images/AvenueM_platform.png',
    mediaType: 'link',
    url: 'https://clients.avemadvisors.com',
  },
  {
    id: 'plai-date',
    title: 'Mobile app for couples',
    context: 'Plai Date',
    thumbnail: '/assets/images/PlaiDate_cover.png',
    mediaType: 'link',
    url: 'https://plaidate.com',
  },
  {
    id: 'panasonic-umi-ces',
    title: 'Umi at CES',
    context: 'Panasonic Well · keynote reveal',
    thumbnail: '/assets/images/PanasonicWell_CES_Keynote.webp',
    mediaType: 'gallery',
    items: [
      { type: 'video', vimeoId: '1042509787', caption: 'The sizzle reel from the main stage of CES, unveiling Umi.' },
      { type: 'video', youtubeId: 'YwpLqT5Rfbg', youtubeStart: 1584, caption: 'The CES 2025 Opening Keynote (jumps to the Umi segment). My responsibility started at the end of the sustainability narrative, incorporating the entire strategy for Panasonic Go, into the insights that drove our work for Panasonic Well and the launches of Umi, the Panasonic Well Partner Collective, and our AI & Data Platform.' },
      { type: 'image', src: '/assets/images/PanasonicWell_CES_Keynote.webp', caption: 'CES keynote reveal of the Umi family wellness agent' },
      { type: 'image', src: '/assets/images/PW_family.jpg', caption: 'Umi at the center of family routines' },
      { type: 'image', src: '/assets/images/Umi.jpg', caption: 'Brand and product identity for Umi' },
    ],
    related: {
      slug: 'panasonic-well',
      hookCopy: 'How a century-old hardware company became an AI services brand →',
    },
  },

  // Stitch Fix "We See You" — anthem film, two portrait films, the
  // Red Carpet experiential, plus the hero imagery and OOH placement,
  // all under one campaign tile.
  {
    id: 'stitch-we-see-you',
    title: '"We See You"',
    context: 'Stitch Fix · brand campaign',
    thumbnail: '/assets/images/SF_WeSeeYou_rev.jpg',
    mediaType: 'gallery',
    items: [
      { type: 'video', youtubeId: 'TMkUk4iICnM', caption: 'The campaign anthem, which broke at the opening of the 2019 Oscars. A storybook approach to show the moment the relationship between stylist and client makes a difference in everyday life.' },
      { type: 'video', youtubeId: 'LEGB6vdfiSc', caption: 'Everybody Deserves to be Seen — "Molly"' },
      { type: 'video', youtubeId: 'g_QmAcKLio0', caption: 'Everybody Deserves to be Seen — "John"' },
      { type: 'video', youtubeId: 'bGpvW9qb7Rk', caption: 'A "Red Carpet" transit takeover in NY and SF, connecting with commuters that maybe didn\'t realize today could be their day to be seen.' },
      { type: 'image', src: '/assets/images/SF_WeSeeYou_rev.jpg', caption: 'Hero key art' },
      { type: 'image', src: '/assets/images/SF_WeSeeYou.jpg', caption: 'Campaign imagery system' },
      { type: 'image', src: '/assets/images/stitchfix_OOH.jpg', caption: 'Out-of-home placement' },
    ],
    related: {
      slug: 'stitch-fix',
      hookCopy: 'The full Stitch Fix relational-brand transformation →',
    },
  },
  {
    id: 'conde-brand-identity',
    title: 'Brand & Identity',
    context: 'Condé · Reimagination',
    thumbnail: 'https://img.youtube.com/vi/QyMo5XJR65I/maxresdefault.jpg',
    mediaType: 'video',
    youtubeId: 'QyMo5XJR65I',
  },

  {
    id: 'panasonic-story-of-our-families',
    title: 'The Story of Our Families',
    context: 'Panasonic Well · insights film',
    thumbnail: '/assets/images/PW_family.jpg',
    mediaType: 'video',
    vimeoId: '1042515442',
    caption:
      'A short documentary on our research families, who we got close with over a 20-month-long ethnographic study. You feel the challenges they face in finding everyday pathways to wellness. These are the people we’re building for.',
    related: {
      slug: 'panasonic-well',
      hookCopy: 'See the insights that drove the Panasonic Well strategy →',
    },
  },
  {
    id: 'panasonic-go',
    title: 'Panasonic Go',
    context: 'Panasonic · Reinvention film',
    thumbnail: '/assets/images/PanasonicGo.jpg',
    mediaType: 'video',
    vimeoId: '1042511393',
    caption:
      'The reinvention video for Panasonic Go — reaching back through the legacy of the business to forge a path forward into the rest of the 21st century.',
    related: {
      slug: 'panasonic-well',
      hookCopy: 'How Panasonic Go shaped the broader strategy →',
    },
  },

  {
    id: 'stitch-keep-summering',
    title: 'Keep Summering',
    context: 'Stitch Fix · summer campaign',
    thumbnail: '/assets/images/SF_KeepSummering.jpg',
    mediaType: 'gallery',
    items: [
      { type: 'video', youtubeId: 'o063fD0Xrok', caption: '"Sprinkler." This cut wasn\'t part of the original production plan, but we loved the intimacy of playing at the sprinklers and ended up cutting all of the marketing speak. Just let the emotions do the work.' },
      { type: 'video', youtubeId: 'GDn6X1wv0X8', caption: '"Giggles" — more vibrant expressions of summer.' },
      { type: 'image', src: '/assets/images/SF_KeepSummering.jpg', caption: 'Campaign key art' },
      { type: 'image', src: '/assets/images/SF_KeepSummeringOOH1.jpg', caption: 'OOH placement 1' },
      { type: 'image', src: '/assets/images/SF_KeepSummeringOOH2.jpg', caption: 'OOH placement 2' },
      { type: 'image', src: '/assets/images/SF_KeepSummeringOOH3.jpg', caption: 'OOH placement 3' },
    ],
    related: {
      slug: 'stitch-fix',
      hookCopy: 'Why the Stitch Fix audience needed a brand, not a discount →',
    },
  },

  // HP "Let's Do Amazing" — launch film, three spot films, key art,
  // and supporting executions in one campaign tile.
  {
    id: 'hp-lets-do-amazing',
    title: 'Let’s Do Amazing',
    context: 'HP · global brand campaign',
    thumbnail: '/assets/images/HP_LetsDoAmazing.jpg',
    mediaType: 'gallery',
    items: [
      { type: 'video', youtubeId: 'WsTyhuj82tE', caption: 'Launch film: A Revolution of Doers.' },
      { type: 'image', src: '/assets/images/HP_LetsDoAmazing.jpg', caption: 'Campaign hero' },
      { type: 'video', youtubeId: 'tikBmF3EvIc', caption: 'Beats by HP — "Making my music sound like it\'s supposed to."' },
      { type: 'image', src: '/assets/images/HP_LDA_beats.jpg', caption: 'Beats by HP collaboration imagery' },
      { type: 'video', youtubeId: 'E_i5xK1iC1o', caption: 'Annie — from print to pixels.' },
      { type: 'video', youtubeId: 'PYIT_tUNuTc', caption: 'DreamWorks — making your dreams, well, work.' },
      { type: 'image', src: '/assets/images/hp_letsdoamazing_rangoli.jpg', caption: 'Rangoli execution, India' },
      { type: 'image', src: '/assets/images/HP_LDA_networking.jpg', caption: 'Enterprise networking moment' },
    ],
    related: {
      slug: 'hp',
      hookCopy: 'How HP rewrote its purpose into Human Progress →',
    },
  },

  {
    id: 'hp-progress-identity',
    title: 'Human Progress',
    context: 'HP · identity system',
    thumbnail: '/assets/images/HP_Progress.jpg',
    mediaType: 'gallery',
    items: [
      { type: 'image', src: '/assets/images/HP_Progress.jpg', caption: 'Purpose lockup' },
      { type: 'image', src: '/assets/images/HP_progressmark.png', caption: 'Progress mark exploration' },
      { type: 'image', src: '/assets/images/HP_purpose_ribbon.jpg', caption: 'Ribbon expression' },
      { type: 'image', src: '/assets/images/HP_purpose_splits.jpg', caption: 'Split format applications' },
    ],
    related: {
      slug: 'hp',
      hookCopy: 'How purpose became the platform for HP’s next decade →',
    },
  },

  // HP "Make It Matter" — the campaign film and the platform
  // architecture image, consolidated into one campaign tile.
  {
    id: 'hp-make-it-matter',
    title: 'Make It Matter',
    context: 'HP · Global Brand Campaign',
    thumbnail: '/assets/images/HP_makeitmatter.jpg',
    mediaType: 'gallery',
    items: [
      { type: 'video', youtubeId: 'SwnHPzczmRk', caption: 'Affirming HP\'s global presence and the range of lives we touch.' },
      { type: 'image', src: '/assets/images/HP_makeitmatter.jpg', caption: 'The platform that preceded Keep Reinventing — internal alignment for 56 business units around a single emotional north star.' },
    ],
    related: {
      slug: 'hp',
      hookCopy: 'See the architecture that unified 56 business units →',
    },
  },

  {
    id: 'conde-passion-progress',
    title: 'Passion & Progress',
    context: 'Condé · brand film',
    thumbnail: 'https://img.youtube.com/vi/EpCuOiNbuZU/maxresdefault.jpg',
    mediaType: 'video',
    youtubeId: 'EpCuOiNbuZU',
  },
  {
    id: 'stitch-brand-transformation',
    title: 'Brand Transformation',
    context: 'Stitch Fix · case film',
    thumbnail: 'https://img.youtube.com/vi/vRWf5_BtaFU/maxresdefault.jpg',
    mediaType: 'video',
    youtubeId: 'vRWf5_BtaFU',
    related: {
      slug: 'stitch-fix',
      hookCopy: 'The full Stitch Fix transformation story →',
    },
  },

    
    {
    id: 'hp-purpose-film',
    title: 'HP’s Purpose',
    context: 'HP · brand film, 2011',
    thumbnail: 'https://img.youtube.com/vi/KA2QxsBo4cY/maxresdefault.jpg',
    mediaType: 'video',
    youtubeId: 'KA2QxsBo4cY',
    caption: 'A 2011 film showcasing HP’s purpose.',
    related: {
      slug: 'hp',
      hookCopy: 'The arc of HP’s purpose work →',
    },
  },
  

  {
    id: 'box-blueprint',
    title: 'The Blueprint',
    context: 'Box · Editorial',
    thumbnail: '/assets/images/Box_theblueprint.jpg',
    mediaType: 'longform',
    intro: 'A printed editorial for the modern enterprise — designed to be read, not glimpsed. The Blueprint translated Box’s Work as One thesis into a thinking-leader’s artifact for CIOs and CDOs.',
    pages: [
      { src: '/assets/images/Box_theblueprint.jpg', caption: 'Cover' },
      { src: '/assets/images/Box_blueprint_digitalworkplace.jpg', caption: 'Digital workplace spread' },
      { src: '/assets/images/Box_blueprint_cognitive.jpg', caption: 'Cognitive enterprise spread' },
      { src: '/assets/images/Box_workasonespread.jpg', caption: 'Work as One pillar spread' },
    ],
    related: {
      slug: 'box',
      hookCopy: 'How Box reframed itself as the enterprise content platform →',
    },
  },
  {
    id: 'stitch-elevated',
    title: 'Elevated',
    context: 'Stitch Fix UK · Launch System',
    thumbnail: '/assets/images/sf_elevated_1.jpg',
    mediaType: 'gallery',
    items: [
      { type: 'image', src: '/assets/images/sf_elevated_1.jpg' },
      { type: 'image', src: '/assets/images/sf_elevated_2.jpg' },
      { type: 'image', src: '/assets/images/sf_elevated_3.jpg' },
      { type: 'image', src: '/assets/images/sf_elevated_4.jpg' },
      { type: 'image', src: '/assets/images/sf_elevated_5.jpg' },
      { type: 'image', src: '/assets/images/sf_elevated_6.jpg' },
      { type: 'image', src: '/assets/images/sf_elevated_7.jpg' },
      { type: 'image', src: '/assets/images/sf_elevated_8.jpg' },
      { type: 'image', src: '/assets/images/sf_elevated_9.jpg' },
      { type: 'image', src: '/assets/images/sf_elevated_10.jpg' },
      { type: 'image', src: '/assets/images/sf_elevated_11.jpg' },
      { type: 'image', src: '/assets/images/sf_elevated_12.jpg' },
    ],
    related: {
      slug: 'stitch-fix',
      hookCopy: 'See how Elevated fit inside the broader Stitch Fix brand reset →',
    },
  },
 {
    id: 'hp-culture-film',
    title: 'The Culture Film',
    context: 'HP · internal branding',
    thumbnail: 'https://img.youtube.com/vi/z1qZG4Z6cQ4/maxresdefault.jpg',
    mediaType: 'video',
    youtubeId: 'z1qZG4Z6cQ4',
    caption:
      'A celebratory film for the entire culture of HP — created to rally the people across the business in their own voices, words and commitment to the future.',
    related: {
      slug: 'hp',
      hookCopy: 'How HP rewrote its purpose into Human Progress →',
    },
  },

 
  // ---------------------------------------------------------------
  // Single-video tiles. Campaigns with multiple connected films are
  // consolidated into gallery tiles above; what remains here is either
  // a standalone film or a chapter-specific piece that doesn't have a
  // larger campaign body to live inside yet.
  // ---------------------------------------------------------------

  // HP — link to /hp
  {
    id: 'hp-progress-mark-film',
    title: 'The Progress Mark reveal',
    context: 'HP · identity launch film',
    thumbnail: 'https://img.youtube.com/vi/91o83XYWdX8/maxresdefault.jpg',
    mediaType: 'video',
    youtubeId: '91o83XYWdX8',
    caption:
      'The film encapsulated the core strategy, then went further to show the number of ways the brand could spring to life — in print and in pixels.',
    related: {
      slug: 'hp',
      hookCopy: 'How HP’s purpose became Human Progress →',
    },
  },
  {
    id: 'hp-true-north',
    title: 'True North',
    context: 'HP · global identity system',
    thumbnail: 'https://img.youtube.com/vi/p3f-xs9BKLA/maxresdefault.jpg',
    mediaType: 'video',
    youtubeId: 'p3f-xs9BKLA',
    caption:
      'Built as an inspiration film for designers, agencies and partners after the Global Identity & Design System was completed. The principles come to life with the range of expressions for a truly dynamic brand.',
    related: {
      slug: 'hp',
      hookCopy: 'See the system that aligned 56 business units →',
      },
    },

  // Box "Work as One" — the print campaign that kickstarted Box's
  // first-ever brand effort, anchored by an MIT Technology Review
  // back-cover series and a BoxWorks17 environmental takeover.
  {
    id: 'box-work-as-one-print',
    title: 'Work as One',
    context: 'Box · print campaign',
    thumbnail: '/assets/images/Box_workasone_wickens.jpg',
    mediaType: 'gallery',
    items: [
      { type: 'image', src: '/assets/images/Box_workasone_shoe.jpg', caption: 'We bought a consistent back cover of MIT Technology Review, but launched with a series of spreads to kickstart the Work as One campaign.' },
      { type: 'image', src: '/assets/images/Box_workasone_lifesciences.jpg', caption: 'Additional print execution: life sciences.' },
      { type: 'image', src: '/assets/images/Box_workasone_car.jpg', caption: 'Additional print execution: automotive.' },
      { type: 'image', src: '/assets/images/Box_workasone_wickens.jpg', caption: 'Environmental takeover of Moscone West during BoxWorks17 — pulling the campaign creative throughout the venue and laying the foundation for the Blueprint for the Future of Work.' },
    ],
    related: {
      slug: 'box',
      hookCopy: 'How Work as One drove a 30% lift in brand desirability →',
    },
  },
  ];

export const findShowcasePieceById = (id: string): ShowcasePiece | undefined =>
  showcasePieces.find((p) => p.id === id);
